import * as i18nData from '@mc-alberta/i18n-data'
import { ACCESS, ACTION, DATA_TEST, ROUTES } from '@/constants'
import { getI18nSingleton } from '@/i18n'
import store from '@/store'
import * as types from '@/store/mutation-types'

const i18n = getI18nSingleton()

/**
 * @typedef {import('vue-router').Route} Route
 * @typedef {import('vue-router').NavigationGuard} NavigationGuard
 */

const params = { skipConfirmation: true }

/**
 * @type {NavigationGuard}
 */
function routeGuard(to, from, next) {
  setPageTitle(to)

  const { isInitialized } = store.state
  // Must initialize once before routing to have login credentials ~Inkh
  if (isInitialized) {
    if (to.params?.showStepTracker) {
      store.dispatch('addStep', to.name)
    }
    navigate(to, from, next)
  } else {
    const checkInitialized = (state) => state.isInitialized
    const toNavigate = () => navigate(to, from, next)
    store.watch(checkInitialized, toNavigate)
  }
}

/**
 * Sets the page title only if specified in the route's meta.title
 *
 * @param {Route} to
 *
 * @return {undefined}
 */
function setPageTitle(to) {
  const entryPaths = ['/', '/enroll', '/return']

  if (to.meta?.title) {
    const prefix = i18n.t('pageTitles.default')
    const title = i18n.t(to.meta.title)
    const prefixedTitle = `${prefix} - ${title}`

    document.title = entryPaths.includes(to.path) ? title : prefixedTitle
  }
}

async function navigate(to, from, next) {
  if (to.params?.logout) {
    await store.dispatch('logout')
  } else {
    setPostLoginPath(to.name)
  }

  // when moving between routes, ensure we don't leave the loading indicator as true
  store.commit(types.SET_ACTION_IS_PENDING, false)

  const route = applyGuards(to)

  // If route is undefined, next(undefined) resolves to next()
  next(route)
}

/**
 * Apply all guard layers to the route
 *
 * @param {Route} to
 * @returns {Route | undefined}
 */
function applyGuards(to) {
  return localeGuards() || postLoginGuards() || accessGuards(to) || undefined
}

/**
 * stores the page name to route to after logging in
 *
 * @param {String} routeName
 * @returns {undefined}
 */
function setPostLoginPath(routeName) {
  const { isLoggedIn } = store.getters
  const restrictedActions = [ACTION.REGISTER, ACTION.BULK_PROVISION]
  const restrictedPostLoginPaths = [
    ROUTES.VERIFY_ACCOUNT,
    ROUTES.VERIFY_MOBILE,
    ROUTES.VERIFY_OPTIONS,
    ROUTES.PROFILE_HOME,
    ROUTES.EMAIL_CONFIRMATION,
    ROUTES.ENTRY,
    ROUTES.LIST_CARDS_UNPROVISIONED,
    ROUTES.REVIEW_PROVISION
  ]
  if (
    !isLoggedIn &&
    !restrictedActions.includes(store.state.action) &&
    !store.state.postLoginPath &&
    !restrictedPostLoginPaths.includes(routeName)
  ) {
    store.commit(types.SET_POST_LOGIN_PATH, routeName)
  }
}

/**
 * Process locale to check if supported, otherwise reroute accordingly
 *
 * @returns {Route|undefined}
 */
function localeGuards() {
  const {
    isLocaleValidated,
    consumer: { countryCode }
  } = store.state.user

  // Only do this only once per app locale change
  if (isLocaleValidated) return
  store.commit(types.SET_LOCALE_VALIDATED, true)

  // If country is live already then exit early
  const isLiveCountry = i18nData.isCountryLive(countryCode)
  if (isLiveCountry) return

  return getUnsupportedRoute(countryCode)
}

/**
 * Get route for unsupported countries
 * "Unsupported" = Coming Soon or Unavailable
 *
 * @param {String} countryCode
 * @returns {Route|undefined}
 */
function getUnsupportedRoute(countryCode) {
  const isCountryComingSoon =
    !i18nData.isCountryLive(countryCode) && i18nData.isCountrySupported(countryCode)
  const isSanctionedCountry = i18nData.isCountrySanctioned(countryCode)
  let route

  if (isCountryComingSoon) {
    route = getComingSoonRoute(countryCode)
  } else if (isSanctionedCountry) {
    route = getUnavailableRoute(countryCode)
  }

  return route
}

/**
 * Route to Coming Soon page
 *
 * @returns {Route}
 */
function getComingSoonRoute() {
  return { name: ROUTES.COMING_SOON, params: getUnsupportedRouteParams() }
}

/**
 * Route to Unavailable page
 *
 * @param {*} countryCode
 * @returns {Route}
 */
function getUnavailableRoute(countryCode) {
  store.dispatch('setMessagePageContent', {
    buttonText: null,
    errorId: `${DATA_TEST.ERROR}-unavailable-country`,
    header: 'unavailable.heading',
    headerInterpolations: {
      countryName: i18nData.getCountryName(countryCode)
    },
    message: null
  })

  return { name: ROUTES.UNAVAILABLE, params: getUnsupportedRouteParams() }
}

/**
 * Get params for unsupported routes
 * It's a function to ensure we're getting updated store data
 *
 * @returns {Object}
 */
function getUnsupportedRouteParams() {
  return { ...params, ...(store.getters.isLoggedIn && { logout: true }) }
}

const routeMap = {
  [ACCESS.LOBBY]: ({ isLoggedIn }) => {
    return isLoggedIn ? { name: ROUTES.PROFILE_HOME, params } : undefined
  },

  [ACCESS.CLEARANCE]: ({ hasClearance }) =>
    !hasClearance ? { name: ROUTES.ENTRY, params } : undefined,

  [ACCESS.PROTECTED]: ({ isLoggedIn, mustReaccept, areUnprovisionedCardsAvailable }) => {
    let name

    if (mustReaccept) {
      name = ROUTES.REACCEPTANCE
    } else if (areUnprovisionedCardsAvailable) {
      name = ROUTES.LIST_CARDS_UNPROVISIONED
    } else if (!isLoggedIn) {
      name = ROUTES.ENTRY
    }

    return name && { name, params }
  }
}

/**
 * Process meta.access and reroute accordingly
 *
 * @param {Route} to
 * @returns {Route|undefined}
 */
function accessGuards(to) {
  const { action, mustAcceptPolicy, mustReaccept, postLoginPath, skipUnprovisionedCards } =
    store.state
  const { isLoggedIn } = store.getters
  const { access } = to.meta
  const isClearanceAction = [
    ACTION.REGISTER,
    ACTION.EMAIL_CONFIRMATION,
    ACTION.BULK_PROVISION
  ].includes(action)
  const hasClearance = isClearanceAction || isLoggedIn
  const shouldSkipUnprovisionedCards =
    skipUnprovisionedCards || !store.state.cards.unprovisionedCards.length
  const areUnprovisionedCardsAvailable = !shouldSkipUnprovisionedCards
  const routeMapFlags = {
    isLoggedIn,
    hasClearance,
    mustAcceptPolicy,
    mustReaccept,
    postLoginPath,
    areUnprovisionedCardsAvailable
  }
  if (Object.prototype.hasOwnProperty.call(routeMap, access)) {
    const newRoute = routeMap[access](routeMapFlags)
    // Only redirect if the new route is different than the to route

    return newRoute && newRoute.name !== to.name ? newRoute : undefined
  }
}

/**
 * Returns the route and clears the existing post login route in the store
 * when the user is routed to the post login route
 *
 * @returns {Route|undefined}
 */
function postLoginGuards() {
  const { postLoginPath, mustAcceptPolicy, mustReaccept, skipUnprovisionedCards } = store.state
  const { isLoggedIn } = store.getters

  if (
    postLoginPath &&
    isLoggedIn &&
    !mustAcceptPolicy &&
    !mustReaccept &&
    !skipUnprovisionedCards
  ) {
    store.commit(types.SET_POST_LOGIN_PATH, null)
    return { name: postLoginPath, params }
  }
}

export default routeGuard
